import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Play, Square, Trash2, Upload } from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { useVoiceStore } from '@/store/voice'
import { describeError } from '@/lib/auth'
import { cx } from '@/lib/cx'
import { useGuildLimits } from '@/lib/guildLimits'
import { playUrl } from '@/lib/sounds'
import { soundUrl, soundboardApi } from '@/lib/soundboard'
import type { Guild, SoundboardSound } from '@/types/api'
import { SoundEmoji } from '../voice/SoundEmoji'
import { SoundForm } from '../voice/SoundForm'
import layer from '../settings/SettingsLayer.module.css'
import gs from './GuildSettingsLayer.module.css'
import styles from './SoundboardSection.module.css'

interface Props {
  guild: Guild
  /** MANAGE_GUILD_EXPRESSIONS: любые звуки. */
  canManage: boolean
  /** CREATE_GUILD_EXPRESSIONS: загружать и править свои. */
  canCreate: boolean
}

/** Настройки сервера → «Звуковая панель»: звуки сервера, загрузка с обрезкой, правка и удаление. */
export function SoundboardSection({ guild, canManage, canCreate }: Props) {
  const me = useAuthStore((s) => s.user)
  const output = useVoiceStore((s) => s.prefs.audiooutput)
  const qc = useQueryClient()
  const sounds = useQuery({ queryKey: ['sounds', guild.id], queryFn: () => soundboardApi.list(guild.id) })
  // сколько можно — по уровню бустов сервера, как считает сервер
  const slots = useGuildLimits(guild.premium_tier).sounds
  const [form, setForm] = useState<{ sound?: SoundboardSound } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [playing, setPlaying] = useState<string | null>(null)
  const audio = useRef<HTMLAudioElement | null>(null)
  const remove = useMutation({
    mutationFn: (id: string) => soundboardApi.remove(guild.id, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sounds', guild.id] }),
    onError: (err) => setError(describeError(err).message),
  })

  useEffect(() => () => audio.current?.pause(), [])

  const listen = async (s: SoundboardSound) => {
    audio.current?.pause()
    if (playing === s.sound_id) {
      setPlaying(null)
      return
    }
    const el = await playUrl(soundUrl(s.sound_id), { volume: s.volume, sinkId: output || undefined })
    audio.current = el
    if (!el) return
    setPlaying(s.sound_id)
    el.onended = () => setPlaying((p) => (p === s.sound_id ? null : p))
  }

  const list = sounds.data ?? []
  const canEdit = (s: SoundboardSound) => canManage || (canCreate && Boolean(me) && s.user?.id === me?.id)
  return (
    <section>
      <h1 className={layer.sectionTitle}>Звуковая панель — {list.length} из {slots}</h1>
      <ul className={layer.points}>
        <li>звуки слышат все в голосовом канале</li>
        <li>MP3, OGG или WAV до 512 КБ</li>
        <li>из длинного файла выберите отрезок до 5,2 секунды</li>
      </ul>
      {canCreate || canManage ? (
        <div className={layer.buttonRow}>
          <button type="button" className={layer.button} disabled={list.length >= slots} onClick={() => setForm({})}><Upload size={16} /> Загрузить звук</button>
        </div>
      ) : null}
      {error ? <p className={gs.error} role="alert">{error}</p> : null}
      {sounds.isPending ? <p className={layer.text}>Загружаем…</p> : null}
      {!sounds.isPending && list.length === 0 ? <p className={layer.text}>Своих звуков пока нет. На панели — стандартные.</p> : null}
      <ul className={styles.list}>
        {list.map((s) => (
          <li key={s.sound_id} className={styles.row}>
            <button type="button" className={cx(styles.play, playing === s.sound_id && styles.playing)} data-tooltip={playing === s.sound_id ? 'Остановить' : 'Прослушать'} aria-label={playing === s.sound_id ? `Остановить «${s.name}»` : `Прослушать «${s.name}»`} onClick={() => void listen(s)}>
              {playing === s.sound_id ? <Square size={14} /> : <Play size={14} />}
            </button>
            <SoundEmoji id={s.emoji_id} name={s.emoji_name} size={22} className={styles.emoji} />
            <span className={styles.text}>
              <span className={styles.name}>{s.name}</span>
              <span className={styles.meta}>
                {s.user ? `Автор: ${s.user.global_name ?? s.user.username} · ` : ''}громкость {Math.round(s.volume * 100)} %
              </span>
            </span>
            {canEdit(s) ? (
              <span className={styles.actions}>
                <button type="button" className={gs.smallButton} data-tooltip="Изменить" aria-label={`Изменить звук ${s.name}`} onClick={() => setForm({ sound: s })}><Pencil size={14} /></button>
                <button type="button" className={cx(gs.smallButton, gs.smallDanger)} data-tooltip="Удалить" aria-label={`Удалить звук ${s.name}`} disabled={remove.isPending} onClick={() => { setError(null); remove.mutate(s.sound_id) }}><Trash2 size={14} /></button>
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {form ? <SoundForm guild={guild} {...(form.sound ? { sound: form.sound } : {})} onClose={() => setForm(null)} /> : null}
    </section>
  )
}
